import * as React from "react";
import { Typography, Button, Box } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useNavigate, useRouteError } from "react-router-dom";

function inFrame() {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
}

export const ErrorPage: React.FunctionComponent<{
    title?: string;
    description?: string;
    debugInfo?: React.ReactNode;
}> = ({ title, description, debugInfo }) => {
    const navigate = useNavigate();
    const onBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);
    const onBackFrame = useCallback(() => {
        window.parent.postMessage({ event: "onCancel" }, "*");
    }, []);

    const isIframe = inFrame();
    const error = useRouteError();

    useEffect(() => {
        console.error(error);
        window.parent.postMessage({ event: "onError", data: error }, "*");
    }, [error]);

    return (
        <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ textAlign: "center", maxWidth: "600px", padding: "20px" }}>
                <img
                    style={{ width: "100%", maxWidth: "300px", borderRadius: 15 }}
                    src="assets/500.jpg"
                    alt="People fixing broken things."
                />
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                    <Typography
                        variant="h5"
                        align="center"
                        paddingTop={4}
                        sx={{ fontWeight: 600, color: "#333" }}
                        gutterBottom
                    >
                        {title || "An unexpected error occurred."}
                    </Typography>
                    <Typography variant="body1" align="center" paddingTop={2} sx={{ color: "#666", lineHeight: 1.6 }}>
                        {description ||
                            "We've been notified and the issue should be fixed soon. Please try again later."}
                    </Typography>
                    {debugInfo && (
                        <Box
                            sx={{
                                marginTop: 3,
                                padding: 2,
                                backgroundColor: "#f5f5f5",
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                                width: "100%",
                                textAlign: "left",
                                maxHeight: "300px",
                                overflowY: "auto",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 600,
                                    color: "#d32f2f",
                                    display: "block",
                                    marginBottom: 1,
                                }}
                            >
                                Debug Information (Development Only)
                            </Typography>
                            <Box sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "#424242" }}>
                                {debugInfo}
                            </Box>
                        </Box>
                    )}
                </Box>
                <Box display="flex" justifyContent={"center"} paddingTop={4} gap={2}>
                    <Button variant="outlined" onClick={isIframe ? onBackFrame : onBack}>
                        {isIframe ? "Quit" : "Go Back"}
                    </Button>
                </Box>
            </div>
        </div>
    );
};

export const MissingPage = () => {
    return (
        <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ textAlign: "center", margin: "auto" }}>
                <img
                    style={{ width: "40vw" }}
                    src="assets/404.svg"
                    alt="Text saying 404 and people searching for something."
                />
                <Typography variant="h6" align="center" paddingTop={5} gutterBottom>
                    Missing Resource Encountered
                </Typography>
                <Typography variant="body1" align="center" paddingTop={2}>
                    You have requested a page that is unavailable on this application. <br />
                    Visit <a href="https://support.spiffcommerce.com">SpiffCommerce</a> to learn more about how you can
                    use us to power product personalization <br /> & automation in your e-commerce platform.
                </Typography>
            </div>
        </div>
    );
};
